import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DocumentEditingState,
  EditableHeaderData,
  CustomFieldValue,
  DocumentEditingCalculations,
  DocumentDraftContact,
} from "../../models/DocumentEditing.model";
import { ProjectItemDTO } from "../../models/BaseItem.model";
import {
  getTemplateHtml,
  getTagsValues,
} from "../thunks/documentEditingThunks";
import { createDocumentTemplateEngine } from "../../utils/documentTemplateEngine";

const documentTemplateEngine = createDocumentTemplateEngine();

const emptyContact: DocumentDraftContact = {
  contactID: 0,
  contactName: "",
  contactIdent: "",
  contactPhone: "",
  contactAddress: "",
  contactEMail: "",
  isManual: false,
};

const emptyHeader: EditableHeaderData = {
  docDate: new Date().toISOString().split("T")[0],
  discountType: 0,
  discountValue: 0,
  vatPercent: 18,
  isRoundedValue: false,
  isVATZero: false,
  remarks: "",
};

const emptyCalculations: DocumentEditingCalculations = {
  itemsSumValue: 0,
  subTotalBeforeDiscount: 0,
  discountAmount: 0,
  subTotal: 0,
  vatValue: 0,
  totalValue: 0,
};

const initialState: DocumentEditingState = {
  templateHtml: null,
  templateAppendiceHtml: null,
  templateContext: null,

  draftItems: [],
  draftHeader: { ...emptyHeader },
  draftCustomFields: [],
  draftContact: { ...emptyContact },

  committedItems: [],
  committedHeader: { ...emptyHeader },
  committedCustomFields: [],
  committedContact: { ...emptyContact },

  calculations: { ...emptyCalculations },

  templateLoading: false,
  tagsLoading: false,
  initLoading: false,
  renderedHtml: null,
  lastRenderId: null,
  activeTemplateRequestId: null,
  activeTagsRequestId: null,
};

const calculateProjectItemLineTotal = (
  item: ProjectItemDTO,
  vatPercent: number,
): number => {
  const qty = Number(item.projectItemQty || 0);
  const price = Number(item.projectItemUnitPrice || 0);
  const rate = Number(item.projectItemCurrencyRate || 1);
  const vatType = Number(item.projectItemVATType || 0);

  let total = qty * price * rate;

  // Same logic as project items table: if unit price includes VAT, convert line total to before-VAT.
  if (vatType === 0 && vatPercent > 0) {
    total = total / (1 + vatPercent / 100);
  }

  return total;
};

// ============================================================
// Slice
// ============================================================

const documentEditingSlice = createSlice({
  name: "documentEditing",
  initialState,
  reducers: {
    // ═══════════════ INIT DRAFT ═══════════════

    initDraftFromCommitted(state) {
      state.draftItems = state.committedItems.map((i) => ({ ...i }));
      state.draftHeader = { ...state.committedHeader };
      state.draftCustomFields = state.committedCustomFields.map((f) => ({
        ...f,
      }));
      state.draftContact = { ...state.committedContact };
    },

    // ═══════════════ INITIALIZE (from external data) ═══════════════

    initializeEditingState(
      state,
      action: PayloadAction<{
        projectItems: ProjectItemDTO[];
        projectItemsHeader: {
          projectItemsHdr_DiscountType: number;
          projectItemsHdr_DiscountValue: number;
          projectItemsHdr_VATPercent: number;
          projectItemsHdr_IsRoundedValue: boolean;
          projectItemsHdr_IsVATZero: boolean;
          projectItemsHdr_Remarks: string;
        } | null;
        customFields: CustomFieldValue[];
        contact?: DocumentDraftContact;
        projectComments?: string;
      }>,
    ) {
      const {
        projectItems,
        projectItemsHeader,
        customFields,
        contact,
        projectComments,
      } = action.payload;

      // Initialize committed state
      state.committedItems = projectItems.map((item) => ({ ...item }));

      if (projectItemsHeader) {
        state.committedHeader = {
          docDate: new Date().toISOString().split("T")[0],
          discountType: projectItemsHeader.projectItemsHdr_DiscountType,
          discountValue: projectItemsHeader.projectItemsHdr_DiscountValue,
          vatPercent: projectItemsHeader.projectItemsHdr_VATPercent,
          isRoundedValue: projectItemsHeader.projectItemsHdr_IsRoundedValue,
          isVATZero: projectItemsHeader.projectItemsHdr_IsVATZero,
          remarks:
            projectItemsHeader.projectItemsHdr_Remarks || projectComments || "",
        };
      } else if (projectComments) {
        state.committedHeader = {
          ...state.committedHeader,
          remarks: projectComments,
        };
      }

      state.committedCustomFields = customFields.map((f) => ({ ...f }));

      if (contact) {
        state.committedContact = { ...contact };
      }

      // Also init draft from committed
      state.draftItems = state.committedItems.map((i) => ({ ...i }));
      state.draftHeader = { ...state.committedHeader };
      state.draftCustomFields = state.committedCustomFields.map((f) => ({
        ...f,
      }));
      state.draftContact = { ...state.committedContact };
    },

    // ═══════════════ DRAFT MUTATIONS ═══════════════

    updateDraftItem(
      state,
      action: PayloadAction<{ itemId: number; field: string; value: any }>,
    ) {
      const { itemId, field, value } = action.payload;
      const item = state.draftItems.find((i) => i.projectItemID === itemId);
      if (item) {
        (item as any)[field] = value;
        if (
          field === "projectItemQty" ||
          field === "projectItemUnitPrice" ||
          field === "projectItemVATType" ||
          field === "projectItemCurrencyRate"
        ) {
          const vatPercent = state.draftHeader.isVATZero
            ? 0
            : state.draftHeader.vatPercent;
          item.projectItemTotalLine = calculateProjectItemLineTotal(
            item,
            vatPercent,
          );
        }
      }
    },

    addDraftItem(state) {
      const newId =
        state.draftItems.length > 0
          ? Math.min(...state.draftItems.map((i) => i.projectItemID)) - 1
          : -1;
      state.draftItems.push({
        projectItemID: newId,
        baseItemID: 0,
        projectItemDesc: "",
        projectItemQty: 1,
        projectItemUnit: "",
        projectItemUnitPrice: 0,
        projectItemCurrency: "₪",
        projectItemCurrencyRate: 1,
        projectItemVATType: 0,
        projectItemVATPercent: state.committedHeader.vatPercent,
        projectItemTotalLine: 0,
      });
    },

    removeDraftItem(state, action: PayloadAction<number>) {
      state.draftItems = state.draftItems.filter(
        (i) => i.projectItemID !== action.payload,
      );
    },

    updateDraftHeader(
      state,
      action: PayloadAction<Partial<EditableHeaderData>>,
    ) {
      state.draftHeader = { ...state.draftHeader, ...action.payload };

      // Recalculate item line totals when VAT settings change, to keep before/after VAT logic consistent.
      if (
        action.payload.vatPercent !== undefined ||
        action.payload.isVATZero !== undefined
      ) {
        const vatPercent = state.draftHeader.isVATZero
          ? 0
          : state.draftHeader.vatPercent;
        state.draftItems = state.draftItems.map((item) => ({
          ...item,
          projectItemTotalLine: calculateProjectItemLineTotal(item, vatPercent),
        }));
      }
    },

    updateDraftCustomField(
      state,
      action: PayloadAction<{
        fieldID: number;
        value: string | number | boolean;
      }>,
    ) {
      const field = state.draftCustomFields.find(
        (f) => f.fieldID === action.payload.fieldID,
      );
      if (field) field.value = action.payload.value;
    },

    updateDraftContact(
      state,
      action: PayloadAction<Partial<DocumentDraftContact>>,
    ) {
      state.draftContact = { ...state.draftContact, ...action.payload };
    },

    setDraftContactFromList(
      state,
      action: PayloadAction<{
        contactID: number;
        contactName: string;
        contactIdent: string;
        contactPhone: string;
        contactEMail: string;
        contactAddress: string;
      }>,
    ) {
      state.draftContact = {
        contactID: action.payload.contactID,
        contactName: action.payload.contactName,
        contactIdent: action.payload.contactIdent || "",
        contactPhone: action.payload.contactPhone || "",
        contactEMail: action.payload.contactEMail || "",
        contactAddress: action.payload.contactAddress || "",
        isManual: false,
      };
    },

    setDraftContactManual(state) {
      state.draftContact = {
        contactID: 0,
        contactName: "",
        contactIdent: "",
        contactPhone: "",
        contactEMail: "",
        contactAddress: "",
        isManual: true,
      };
    },

    // ═══════════════ COMMIT (draft → committed) ═══════════════

    commitItems(state) {
      state.committedItems = state.draftItems.map((i) => ({ ...i }));
      state.draftItems = state.committedItems.map((i) => ({ ...i }));
    },

    commitHeader(state) {
      state.committedHeader = { ...state.draftHeader };
      state.draftHeader = { ...state.committedHeader };
    },

    commitCustomFields(state) {
      state.committedCustomFields = state.draftCustomFields.map((f) => ({
        ...f,
      }));
      state.draftCustomFields = state.committedCustomFields.map((f) => ({
        ...f,
      }));
    },

    commitContact(state) {
      state.committedContact = { ...state.draftContact };
      state.draftContact = { ...state.committedContact };
    },

    // ═══════════════ CALCULATIONS ═══════════════

    setCalculations(state, action: PayloadAction<DocumentEditingCalculations>) {
      state.calculations = action.payload;
    },

    // ═══════════════ TEMPLATE ═══════════════

    setTemplateContext(
      state,
      action: PayloadAction<Record<string, any> | null>,
    ) {
      state.templateContext = action.payload;
    },

    setRenderedHtml(
      state,
      action: PayloadAction<{ html: string; renderId: string }>,
    ) {
      state.renderedHtml = action.payload.html;
      state.lastRenderId = action.payload.renderId;
    },

    // ═══════════════ CLEAR ═══════════════

    clearEditingState() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getTemplateHtml.pending, (state, action) => {
      state.templateLoading = true;
      state.initLoading = true;
      state.activeTemplateRequestId = action.meta.requestId;
    });
    builder.addCase(getTemplateHtml.fulfilled, (state, action) => {
      if (state.activeTemplateRequestId !== action.meta.requestId) {
        return;
      }
      state.templateLoading = false;
      state.templateHtml = action.payload.documentBaseHTML;
      state.templateAppendiceHtml =
        action.payload.documentLegalAppendiceHTML || null;
      if (!state.tagsLoading) state.initLoading = false;
    });
    builder.addCase(getTemplateHtml.rejected, (state, action) => {
      if (state.activeTemplateRequestId !== action.meta.requestId) {
        return;
      }
      state.templateLoading = false;
      state.initLoading = false;
    });

    builder.addCase(getTagsValues.pending, (state, action) => {
      state.tagsLoading = true;
      state.initLoading = true;
      state.activeTagsRequestId = action.meta.requestId;
    });
    builder.addCase(getTagsValues.fulfilled, (state, action) => {
      if (state.activeTagsRequestId !== action.meta.requestId) {
        return;
      }
      state.tagsLoading = false;
      if (!state.templateLoading) state.initLoading = false;

      const tagsValues = action.payload.tagsValues;
      if (tagsValues && tagsValues.length > 0) {
        state.templateContext = documentTemplateEngine.compileTagsToContext(
          tagsValues.map((tag) => ({
            tagKey: tag.tagKey,
            tagValue: tag.tagValue,
          })),
        );
      }
    });
    builder.addCase(getTagsValues.rejected, (state, action) => {
      if (state.activeTagsRequestId !== action.meta.requestId) {
        return;
      }
      state.tagsLoading = false;
      state.initLoading = false;
    });
  },
});

export const {
  initDraftFromCommitted,
  initializeEditingState,
  updateDraftItem,
  addDraftItem,
  removeDraftItem,
  updateDraftHeader,
  updateDraftCustomField,
  updateDraftContact,
  setDraftContactFromList,
  setDraftContactManual,
  commitItems,
  commitHeader,
  commitCustomFields,
  commitContact,
  setCalculations,
  setTemplateContext,
  setRenderedHtml,
  clearEditingState,
} = documentEditingSlice.actions;

export default documentEditingSlice.reducer;
